import { CurrencyPipe } from '@angular/common';
import {
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../../../core/services/toast/toast.service';
import { AuthService } from '../../../modules/auth/services/auth/auth.service';
import { CpSubscriptionTrialService } from '../../../core/services/subscription-trial/cp-subscription-trial.service';
import {
  executeSubscriptionCheckout,
  isSubscriptionPaymentCancelledError,
} from '../../../services/subscription/utils/subscription-payment-checkout.util';
import { CpSubscriptionPlanOption } from '../../../services/cp-subscription/cp-subscription.service';
import { CpSubscriptionPaymentApiService } from '../../../services/subscription/cp-subscription-payment-api.service';
import { SubscriptionPaymentConfirmModalComponent } from '../subscription-payment-confirm-modal/subscription-payment-confirm-modal.component';
import { SubscriptionPaymentPurpose } from '../../../services/subscription/models/subscription-payment.model';
import {
  billingCycleLabel,
  buildSubscriptionPaymentConfirmAmounts,
  planAmountForCycle,
  type SubscriptionPaymentConfirmState,
} from '../../../services/subscription/utils/subscription-payment.util';

@Component({
  selector: 'app-subscription-upgrade-modal',
  standalone: true,
  imports: [CurrencyPipe, SubscriptionPaymentConfirmModalComponent],
  templateUrl: './subscription-upgrade-modal.component.html',
  styleUrls: ['./subscription-upgrade-modal.component.css'],
})
export class SubscriptionUpgradeModalComponent implements OnInit {
  private readonly trial = inject(CpSubscriptionTrialService);
  private readonly subscriptionPayment = inject(CpSubscriptionPaymentApiService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);

  readonly closable = input(false);
  readonly heading = input('');
  readonly subheading = input('');
  readonly planCatalog = input<CpSubscriptionPlanOption[] | null>(null);

  readonly dismissed = output<void>();
  readonly upgraded = output<void>();

  readonly summary = this.trial.summary;
  readonly loading = this.trial.loading;
  readonly upgradingPlanId = this.trial.upgradingPlanId;
  readonly selectedPlanId = signal<string | null>(null);
  readonly billingCycle = signal<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY');
  readonly paymentConfirm = signal<SubscriptionPaymentConfirmState | null>(null);
  readonly paymentProcessing = signal(false);
  readonly planOverlayHidden = signal(false);

  readonly visiblePlans = computed(() => {
    const catalog = this.planCatalog();
    const source = catalog ?? this.trial.plans();
    return source.filter((plan) => this.isPaidUpgradePlan(plan));
  });

  readonly selectedPlan = computed(() => {
    const id = this.selectedPlanId();
    if (!id) {
      return null;
    }
    return this.visiblePlans().find((plan) => plan.id === id) ?? null;
  });

  readonly recommendedPlanId = computed(() => {
    const plans = this.visiblePlans();
    if (!plans.length) {
      return null;
    }
    const currentId = this.summary()?.plan?.id;
    const currentPrice = Number(this.summary()?.plan?.pricing?.monthly ?? 0);
    const higher = plans.find(
      (plan) => plan.id !== currentId && Number(plan.pricing.monthly) > currentPrice,
    );
    return (higher ?? plans.find((plan) => plan.id !== currentId) ?? plans[0]).id;
  });

  readonly hasQuarterlyPricing = computed(() =>
    this.visiblePlans().some((plan) => Number(plan.pricing.quarterly ?? 0) > 0),
  );

  readonly hasYearlyPricing = computed(() =>
    this.visiblePlans().some((plan) => Number(plan.pricing.yearly ?? 0) > 0),
  );

  isCurrentPlan(plan: CpSubscriptionPlanOption): boolean {
    const currentId = this.summary()?.plan?.id;
    return Boolean(currentId) && currentId === plan.id && this.summary()?.plan?.isFree !== true;
  }

  isPaidUpgradePlan(plan: CpSubscriptionPlanOption): boolean {
    if (plan.isFree === true) {
      return false;
    }
    const code = (plan.code ?? '').trim().toUpperCase();
    if (code === 'PLAN_FREE' || code === 'PLAN_CP_FREE') {
      return false;
    }
    if (Number(plan.pricing?.monthly ?? 0) <= 0) {
      return false;
    }
    const name = (plan.name ?? '').trim().toLowerCase();
    return name !== 'free plan' && name !== 'free trial';
  }

  setBillingCycle(cycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY'): void {
    this.billingCycle.set(cycle);
  }

  planAmount(plan: CpSubscriptionPlanOption): number {
    return planAmountForCycle(plan, this.billingCycle());
  }

  cycleSuffix(): string {
    const cycle = this.billingCycle();
    if (cycle === 'YEARLY') {
      return '/ year';
    }
    if (cycle === 'QUARTERLY') {
      return '/ quarter';
    }
    return '/ month';
  }

  formatLimit(value: number | null | undefined): string {
    if (value == null || value >= 999999) {
      return 'Unlimited';
    }
    return String(value);
  }

  readonly titleText = computed(() => {
    const heading = this.heading().trim();
    if (heading) {
      return heading;
    }
    return this.summary()?.plan?.isFree
      ? 'Your free trial has ended'
      : 'Your plan trial has ended';
  });

  readonly subtitleText = computed(() => {
    const subheading = this.subheading().trim();
    if (subheading) {
      return subheading;
    }
    return 'Select a plan, then continue to payment.';
  });

  ngOnInit(): void {
    this.trial.refresh();
  }

  selectPlan(plan: CpSubscriptionPlanOption): void {
    this.selectedPlanId.set(plan.id);
  }

  dismiss(): void {
    if (this.closable() && !this.paymentProcessing()) {
      this.dismissed.emit();
    }
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    this.dismiss();
  }

  continueToPayment(): void {
    const plan = this.selectedPlan();
    if (!plan || this.upgradingPlanId() || this.paymentProcessing()) {
      return;
    }
    const paymentAmounts = buildSubscriptionPaymentConfirmAmounts(
      planAmountForCycle(plan, this.billingCycle()),
    );
    this.paymentConfirm.set({
      title: `Upgrade to ${plan.name}`,
      description:
        plan.tagline ??
        `Unlock up to ${this.formatLimit(plan.projectLimit)} projects and ${this.formatLimit(plan.userLimit)} builder connections.`,
      ...paymentAmounts,
      billingLabel: billingCycleLabel(this.billingCycle()),
      processingKey: plan.id,
      initiateRequest: {
        purpose: SubscriptionPaymentPurpose.UPGRADE,
        planId: plan.id,
        billingCycle: this.billingCycle(),
      },
    });
  }

  cancelPaymentConfirm(): void {
    if (!this.paymentProcessing()) {
      this.paymentConfirm.set(null);
    }
  }

  proceedPaymentConfirm(): void {
    const confirm = this.paymentConfirm();
    if (!confirm || this.paymentProcessing()) {
      return;
    }

    this.trial.setUpgrading(confirm.processingKey);
    this.paymentProcessing.set(true);

    executeSubscriptionCheckout({
      paymentService: this.subscriptionPayment,
      initiateRequest: confirm.initiateRequest,
      merchantName: 'BrickHub',
      description: confirm.title,
      onCheckoutOpening: () => {
        this.paymentConfirm.set(null);
        this.planOverlayHidden.set(true);
      },
    })
      .pipe(
        finalize(() => {
          this.paymentProcessing.set(false);
          this.paymentConfirm.set(null);
          this.planOverlayHidden.set(false);
          this.trial.clearUpgrading();
        }),
      )
      .subscribe({
        next: (res) => {
          const entitlements = res.entitlements ?? [];
          this.auth.updateEntitlements(entitlements);
          this.trial.markUpgradeCompleted(entitlements);
          this.toast.success(res.message || 'Plan upgraded successfully');
          this.trial.refresh();
          this.upgraded.emit();
          if (this.closable()) {
            this.dismissed.emit();
          }
        },
        error: (err: unknown) => {
          if (isSubscriptionPaymentCancelledError(err)) {
            this.toast.error('Payment cancelled', {
              message: 'You can try again when ready.',
            });
            return;
          }
          this.toast.error('Upgrade failed', {
            message: this.resolveUpgradeErrorMessage(err),
          });
        },
      });
  }

  private resolveUpgradeErrorMessage(err: unknown): string {
    const httpErr = err as {
      status?: number;
      error?: { message?: string | string[] };
      message?: string;
    };
    const rawMessage = httpErr?.error?.message ?? httpErr?.message;
    if (Array.isArray(rawMessage)) {
      return rawMessage.join('; ');
    }
    if (typeof rawMessage === 'string' && rawMessage.trim()) {
      return rawMessage.trim();
    }
    if (httpErr?.status === 0) {
      return 'Cannot reach billing service. Is admin running on port 3007?';
    }
    return 'Please try again.';
  }
}
